import type { Building } from "building-types/building/buildingTypes";
import type {
  PropKAM,
  PropKAMOptions,
  PropKeyAttributes,
  PropKeyMeasures,
} from "building-types/dataSequences/builtin/PropKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { keyAttributeNew, keyMeasureNew, keyAttributeOptionsNew } from "doc/utils/dataSequenceTools";
import {
  DOC_ENTITY_COLUMN_NAME,
  SortBy,
  categoryColumnNew,
  configurableTableNew,
  identifierColumnNew,
  numberColumnNew,
  rowMetadataNew,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import { removeUnderscore } from "lib/house/dataSequences/dataSequenceFormatting";

export function makeInteriorPropsTable(props: Table<Prop>, propCatalog: PropAttributeTable) {
  return configurableTableNew<Prop, Prop>({
    iterables: props,
    requirement: isInteriorProp(propCatalog),
    columns: [
      categoryColumnNew("Interior Items"),
      stringColumnNew({
        name: "Description",
        fieldFunc: (prop) => propInteriorDescription(prop, propCatalog),
      }),
      numberColumnNew({
        name: "Level",
        fieldFunc: (prop) => ensureDefined(prop.room).level.floorIndex + 1,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Room",
        fieldFunc: (prop) => ensureDefined(prop.room).nameInPlan ?? ensureDefined(prop.room).type,
        stringFormatter: removeUnderscore,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Room ID",
        fieldFunc: (prop) => ensureDefined(prop.room).id,
        hidden: true,
        useInDiff: false,
      }),
    ],
  });
}

export function makePropsTable(building: Building) {
  const props: PropKAM[] = buildingPropKAMs(building);

  return configurableTableNew<PropKAM, PropKAM>({
    iterables: props,
    requirement: (prop) => prop,
    columns: [
      categoryColumnNew("Fixtures & Appliances"),
      stringColumnNew({
        name: "Type",
        fieldFunc: (prop) => prop.attributes.type.value,
        useInDiff: false,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Description",
        fieldFunc: (prop) => prop.attributes.description.value,
        sortBy: SortBy.Ascending,
      }),
    ],
    identifiers: [
      identifierColumnNew({
        name: DOC_ENTITY_COLUMN_NAME,
        identifierFunc: (prop) => prop.handles.map(docEntityHandleHash).join(",") || "0",
      }),
    ],
    rowMetadata: rowMetadataNew({
      metadataFunc: (prop) => {
        const entityName = `${prop.attributes.type.value}_${prop.attributes.id.value}`;
        return { entityName, entityType: "PROPS" };
      },
    }),
  });
}

function isInteriorProp(propCatalog: PropAttributeTable) {
  return (prop: Prop): Prop | null => {
    const propAttributes = propCatalog[prop.src];

    if (!propAttributes) {
      return null;
    }

    if (propAttributes.propAssetId === undefined) {
      return null;
    }

    if (!propAttributes.takeoffDescription) {
      return null;
    }

    return prop;
  };
}

function propInteriorDescription(prop: Prop, propCatalog: PropAttributeTable): string {
  const description = propCatalog[prop.src]?.takeoffDescription ?? "";
  return description;
}

function filterProcureProp(propTypes: PropAttributeTable) {
  return (prop: Prop): Prop | null => {
    const propAttributes = propTypes[prop.src];

    if (!propAttributes) {
      return null;
    }

    if (propAttributes.propAssetId === undefined) {
      return null;
    }

    if (!propAttributes.category) {
      return null;
    }

    return prop;
  };
}

function propProcureDescription(prop: Prop, propTypes: PropAttributeTable): string {
  const description = propTypes[prop.src]?.takeoffDescription || "";
  return description;
}

export function propEntityName(prop: Prop, propTypes: PropAttributeTable): string {
  const propAttributes = propTypes[prop.src];
  return `${propAttributes.category}_${propAttributes.id}`;
}

export function getProcureProps(building: Building): Prop[] {
  const { props, settings } = building;
  return Object.values(props).filter(filterProcureProp(settings.props));
}

export function getPropEntityObject(prop: Prop, { settings }): { [key: string]: string } {
  const propAttributes = settings.props[prop.src];
  return {
    entityName: propEntityName(prop, settings.props),
    description: propAttributes.takeoffDescription ?? "",
  };
}

export function makePropKAM(prop: Prop, propTypes: PropAttributeTable): PropKAM {
  return {
    type: "PROP",
    attributes: {
      id: keyAttributeNew(propTypes[prop.src].id),
      type: keyAttributeNew(propTypes[prop.src].category ?? ""),
      propName: keyAttributeNew(propTypes[prop.src].name),
      description: keyAttributeNew(propProcureDescription(prop, propTypes)),
      entityName: keyAttributeNew(propEntityName(prop, propTypes)),
    },
    measures: {
      count: keyMeasureNew(1),
    },
    handles: [buildingEntityPickHandle(prop)],
  };
}
export const EMPTY_PROP_KAM: PropKAM = {
  type: "PROP",
  attributes: {
    id: keyAttributeNew(""),
    type: keyAttributeNew(""),
    propName: keyAttributeNew(""),
    description: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultPropKAMOptions(): PropKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {},
  };
}
export const defaultPropKAMSort = (a: PropKAM, b: PropKAM) => {
  return (
    a.attributes.type.value.localeCompare(b.attributes.type.value) ||
    a.attributes.description.value.localeCompare(b.attributes.description.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingPropKAMs({ props, settings }: Building, filter?: BuildingEntityFilter): PropKAM[] {
  const filtered = Object.values(props).filter(filterProcureProp(settings.props));
  const result: PropKAM[] = [];

  for (const propEntity of filtered) {
    if (filter && !filter(propEntity)) {
      continue;
    }
    result.push(makePropKAM(propEntity, settings.props));
  }
  return result;
}

export const PROP_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<PropKeyAttributes, PropKeyMeasures> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    id: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_PROP: (keyof PropKeyAttributes)[] = ["type", "id"];
